import { useEffect, useMemo, useRef, useState } from 'react';
import {
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  setHours,
  startOfDay,
  startOfWeek
} from 'date-fns';
import clsx from 'clsx';

import type { CalendarEvent, DayPulse } from '../types';
import { eventEndDayInclusive, eventStartDay } from '../event-day';
import {
  type EventMovePatch,
  moveByDays,
  moveByMinutes,
  resizeEndByMinutes
} from '../event-drag';
import { retintEventBackground, retintEventForeground, useResolvedTheme } from '../theme';
import { useDragGesture } from '../useDragGesture';
import { useNow } from '../useNow';
import DailyNotePin from './DailyNotePin';
import DayWeather from './DayWeather';
import WeekWeatherRow from './WeekWeatherRow';
import type { WeatherView } from '../weather';

const MIN_HOUR_HEIGHT = 44;
const HOURS = 24;
const TIME_COL_WIDTH = 56;
const ALL_DAY_ROW_MIN = 30;
const SNAP_MIN = 15;
const RESIZE_HANDLE_PX = 6;

interface Props {
  anchor: Date;
  events: CalendarEvent[];
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  dayPulse?: DayPulse | null;
  onPickEvent?: (id: string, rect: DOMRect) => void;
  onCreateAt?: (d: Date, rect: DOMRect) => void;
  onEventContextMenu?: (id: string, x: number, y: number) => void;
  canMoveEvent?: (e: CalendarEvent) => boolean;
  onMoveEvent?: (e: CalendarEvent, patch: EventMovePatch) => void;
  noteDates?: Set<string>;
  todayKey?: string | null;
  onOpenDailyNote?: (date: string, rect: DOMRect) => void;
  weather?: WeatherView | null;
  onOpenWeather?: (date: string, rect: DOMRect) => void;
}

type LaidEvent = {
  event: CalendarEvent;
  startMin: number;
  endMin: number;
  col: number;
  cols: number;
};

// Compact h:mma range, dropping the meridiem on the start when both halves
// share one (so "8:00 – 9:00am" instead of "8:00am – 9:00am").
function formatTimeRange(start: Date, end: Date): string {
  const sa = format(start, 'a').toLowerCase();
  const ea = format(end, 'a').toLowerCase();
  const startStr = sa === ea ? format(start, 'h:mm') : `${format(start, 'h:mm')}${sa}`;
  const endStr = `${format(end, 'h:mm')}${ea}`;
  return `${startStr} – ${endStr}`;
}

// Pack overlapping events into side-by-side columns. Greedy: walk sorted by
// start time, place each event in the lowest column index where the previous
// occupant ended at or before this event's start.
function layoutDay(events: CalendarEvent[]): LaidEvent[] {
  type Slot = { event: CalendarEvent; startMin: number; endMin: number };
  const slots: Slot[] = [];
  for (const e of events) {
    if (e.all_day || !e.start_time || !e.end_time) continue;
    const start = new Date(e.start_time);
    const end = new Date(e.end_time);
    // Wall-clock minutes, not elapsed-since-midnight: the hour axis, now-line
    // and click-to-create targets are all wall-clock, and on DST-change days
    // elapsed minutes would draw every event an hour off the axis.
    const startMin = start.getHours() * 60 + start.getMinutes();
    const durationMin = (end.getTime() - start.getTime()) / 60_000;
    const endMin = Math.min(24 * 60, startMin + durationMin);
    if (endMin <= startMin) continue;
    slots.push({ event: e, startMin, endMin });
  }
  slots.sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  // Cluster overlapping runs so we can compute a stable per-cluster column count.
  const clusters: Slot[][] = [];
  let current: Slot[] = [];
  let currentEnd = -Infinity;
  for (const s of slots) {
    if (current.length === 0 || s.startMin < currentEnd) {
      current.push(s);
      currentEnd = Math.max(currentEnd, s.endMin);
    } else {
      clusters.push(current);
      current = [s];
      currentEnd = s.endMin;
    }
  }
  if (current.length) clusters.push(current);

  const out: LaidEvent[] = [];
  for (const cluster of clusters) {
    const colEnd: number[] = [];
    const assigned: number[] = [];
    for (const s of cluster) {
      let c = colEnd.findIndex((e) => e <= s.startMin);
      if (c === -1) {
        c = colEnd.length;
        colEnd.push(s.endMin);
      } else {
        colEnd[c] = s.endMin;
      }
      assigned.push(c);
    }
    const cols = colEnd.length;
    for (let i = 0; i < cluster.length; i++) {
      out.push({
        event: cluster[i].event,
        startMin: cluster[i].startMin,
        endMin: cluster[i].endMin,
        col: assigned[i],
        cols
      });
    }
  }
  return out;
}

export default function WeekView({
  anchor,
  events,
  weekStartsOn = 0,
  dayPulse = null,
  onPickEvent,
  onCreateAt,
  onEventContextMenu,
  canMoveEvent,
  onMoveEvent,
  noteDates,
  todayKey,
  onOpenDailyNote,
  weather = null,
  onOpenWeather
}: Props) {
  const theme = useResolvedTheme();
  const today = useNow();
  const days = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(anchor, { weekStartsOn }),
        end: endOfWeek(anchor, { weekStartsOn })
      }),
    [anchor, weekStartsOn]
  );

  const allDay: { event: CalendarEvent; startDay: Date; endDay: Date }[] = [];
  const eventsByDay = new Map<string, CalendarEvent[]>();
  for (const e of events) {
    const start = eventStartDay(e);
    if (!start) continue;
    const end = eventEndDayInclusive(e) ?? start;
    if (e.all_day || !isSameDay(start, end)) {
      allDay.push({ event: e, startDay: start, endDay: end });
      continue;
    }
    const k = format(start, 'yyyy-MM-dd');
    const list = eventsByDay.get(k) ?? [];
    list.push(e);
    eventsByDay.set(k, list);
  }

  // All-day bar layout: assign rows so overlapping bars stack vertically.
  // The backend can return events that overlap the requested UTC window but
  // sit on a day outside the user's local week (e.g. a Monday all-day event
  // is stored at midnight UTC, which is the prior afternoon in Pacific). Use
  // signed day deltas so events fully before/after the week are dropped,
  // and partially-overlapping ranges clamp to the visible columns.
  const firstDay = days[0];
  const lastDay = days[6];
  const allDayBars: { event: CalendarEvent; startCol: number; endCol: number; row: number }[] = [];
  {
    const rowEnds: number[] = [];
    const sorted = [...allDay].sort(
      (a, b) => a.startDay.getTime() - b.startDay.getTime() || b.endDay.getTime() - a.endDay.getTime()
    );
    for (const { event, startDay, endDay } of sorted) {
      if (endDay < firstDay || startDay > lastDay) continue;
      const sc = Math.max(0, differenceInCalendarDays(startDay, firstDay));
      const ec = Math.min(6, differenceInCalendarDays(endDay, firstDay));
      let row = rowEnds.findIndex((e) => e < sc);
      if (row === -1) {
        row = rowEnds.length;
        rowEnds.push(ec);
      } else {
        rowEnds[row] = ec;
      }
      allDayBars.push({ event, startCol: sc, endCol: ec, row });
    }
  }
  const allDayRows = allDayBars.reduce((m, b) => Math.max(m, b.row + 1), 0);
  const allDayHeight = Math.max(ALL_DAY_ROW_MIN, allDayRows * 24 + 8);

  // Hour height stretches to fill the scroll container so 24h occupies the
  // viewport when it fits. Below MIN_HOUR_HEIGHT we scroll instead.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hourHeight, setHourHeight] = useState(MIN_HOUR_HEIGHT);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function measure() {
      if (!el) return;
      const fit = el.clientHeight / HOURS;
      setHourHeight(Math.max(MIN_HOUR_HEIGHT, fit));
    }
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scroll body to 7am (or to current time on today) on first paint, but only
  // when the grid is actually scrollable (i.e. hourHeight bottomed out at
  // MIN_HOUR_HEIGHT). Runs once.
  const initialScrolled = useRef(false);
  useEffect(() => {
    if (initialScrolled.current) return;
    const el = scrollRef.current;
    if (!el || hourHeight <= 0) return;
    if (HOURS * hourHeight <= el.clientHeight) {
      initialScrolled.current = true;
      return;
    }
    const showToday = days.some((d) => isSameDay(d, today));
    const targetHour = showToday ? Math.max(0, today.getHours() - 2) : 7;
    el.scrollTop = targetHour * hourHeight;
    initialScrolled.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourHeight]);

  const hourLabels = useMemo(
    () =>
      Array.from({ length: HOURS }, (_, h) => {
        const t = setHours(startOfDay(new Date()), h);
        return format(t, 'h aaaa').toLowerCase().replace('.', '');
      }),
    []
  );

  const nowMin = today.getHours() * 60 + today.getMinutes();

  // Drag-and-drop state. Three flavours: drag a timed event, resize the
  // bottom edge of a timed event, drag an all-day bar across days.
  type Drag =
    | { kind: 'move-timed'; eventId: string; daysDelta: number; deltaMin: number }
    | { kind: 'resize-end'; eventId: string; deltaMin: number }
    | { kind: 'move-allday'; eventId: string; daysDelta: number };
  const [drag, setDrag] = useState<Drag | null>(null);
  const { startDrag, consumeDragClick } = useDragGesture();

  const hoursGridRef = useRef<HTMLDivElement>(null);
  const allDayGridRef = useRef<HTMLDivElement>(null);
  const [colWidth, setColWidth] = useState(0);
  useEffect(() => {
    const el = hoursGridRef.current;
    if (!el) return;
    function measure() {
      if (el) setColWidth(Math.max(0, (el.clientWidth - TIME_COL_WIDTH) / 7));
    }
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  function dayIndexAt(x: number, source: 'hours' | 'allday'): number | null {
    const ref = source === 'hours' ? hoursGridRef.current : allDayGridRef.current;
    if (!ref) return null;
    const r = ref.getBoundingClientRect();
    const offsetLeft = source === 'hours' ? TIME_COL_WIDTH : 0;
    const width = source === 'hours' ? r.width - TIME_COL_WIDTH : r.width;
    if (width <= 0) return null;
    const localX = x - r.left - offsetLeft;
    if (localX < 0 || localX > width) return null;
    const idx = Math.floor((localX / width) * 7);
    if (idx < 0 || idx > 6) return null;
    return idx;
  }

  function snapMinutes(dyPx: number): number {
    const raw = (dyPx / hourHeight) * 60;
    return Math.round(raw / SNAP_MIN) * SNAP_MIN;
  }

  function commitMove(event: CalendarEvent, daysDelta: number, deltaMin: number) {
    let cur = event;
    if (daysDelta !== 0) {
      const p = moveByDays(cur, daysDelta);
      if (!p) return;
      cur = { ...cur, start_time: p.start_time, end_time: p.end_time, all_day: p.all_day };
    }
    if (deltaMin !== 0) {
      const p = moveByMinutes(cur, deltaMin);
      if (!p) return;
      cur = { ...cur, start_time: p.start_time, end_time: p.end_time, all_day: p.all_day };
    }
    if (!cur.start_time || !cur.end_time) return;
    onMoveEvent?.(event, {
      start_time: cur.start_time,
      end_time: cur.end_time,
      all_day: cur.all_day
    });
  }

  function beginTimedMove(event: CalendarEvent, originDayIdx: number, ev: React.MouseEvent) {
    if (!onMoveEvent || !canMoveEvent?.(event)) return;
    const startY = ev.clientY;
    startDrag(ev, {
      cursor: 'grabbing',
      onMove: (mev) => {
        const cur = dayIndexAt(mev.clientX, 'hours');
        const daysDelta = cur != null ? cur - originDayIdx : 0;
        const deltaMin = snapMinutes(mev.clientY - startY);
        setDrag({ kind: 'move-timed', eventId: event.id, daysDelta, deltaMin });
      },
      onEnd: (mev) => {
        const cur = dayIndexAt(mev.clientX, 'hours');
        const daysDelta = cur != null ? cur - originDayIdx : 0;
        const deltaMin = snapMinutes(mev.clientY - startY);
        setDrag(null);
        if (daysDelta === 0 && deltaMin === 0) return;
        commitMove(event, daysDelta, deltaMin);
      },
      onCancel: () => setDrag(null)
    });
  }

  function beginTimedResize(event: CalendarEvent, ev: React.MouseEvent) {
    if (!onMoveEvent || !canMoveEvent?.(event)) return;
    const startY = ev.clientY;
    startDrag(ev, {
      cursor: 'ns-resize',
      axis: 'y',
      onMove: (mev) => {
        setDrag({ kind: 'resize-end', eventId: event.id, deltaMin: snapMinutes(mev.clientY - startY) });
      },
      onEnd: (mev) => {
        const deltaMin = snapMinutes(mev.clientY - startY);
        setDrag(null);
        if (deltaMin === 0) return;
        const patch = resizeEndByMinutes(event, deltaMin);
        if (patch) onMoveEvent?.(event, patch);
      },
      onCancel: () => setDrag(null)
    });
  }

  function beginAllDayMove(event: CalendarEvent, ev: React.MouseEvent) {
    if (!onMoveEvent || !canMoveEvent?.(event)) return;
    const origin = dayIndexAt(ev.clientX, 'allday');
    if (origin == null) return;
    const originIdx: number = origin;
    startDrag(ev, {
      cursor: 'grabbing',
      onMove: (mev) => {
        const cur = dayIndexAt(mev.clientX, 'allday');
        const daysDelta = cur != null ? cur - originIdx : 0;
        setDrag({ kind: 'move-allday', eventId: event.id, daysDelta });
      },
      onEnd: (mev) => {
        const cur = dayIndexAt(mev.clientX, 'allday');
        const daysDelta = cur != null ? cur - originIdx : 0;
        setDrag(null);
        if (daysDelta === 0) return;
        const patch = moveByDays(event, daysDelta);
        if (patch) onMoveEvent?.(event, patch);
      },
      onCancel: () => setDrag(null)
    });
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header row */}
      <div
        className="grid border-b border-[var(--border)] bg-[var(--bg)]"
        style={{ gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))` }}
      >
        <div className="border-r border-[var(--border)]" />
        {days.map((day) => {
          const isToday = isSameDay(day, today);
          const dayKey = format(day, 'yyyy-MM-dd');
          const hasNote = noteDates?.has(dayKey) ?? false;
          return (
            <div
              key={day.toISOString()}
              className="group relative flex items-center justify-between border-r border-[var(--border)] px-3 py-2 last:border-r-0"
            >
              <div className="flex items-baseline gap-2">
                <span
                  className={clsx(
                    'text-[0.7rem] uppercase',
                    isToday ? 'text-[var(--accent)]' : 'text-[var(--text-faint)]'
                  )}
                >
                  {format(day, 'EEE')}
                </span>
                <span
                  className={clsx(
                    'rounded-full px-[6px] text-[1.05rem] font-semibold',
                    isToday && 'bg-[var(--accent)] text-white'
                  )}
                >
                  {format(day, 'd')}
                </span>
              </div>
              <div className="flex min-w-0 items-center gap-1">
                {weather?.prefs.overlays.includes('week') && onOpenWeather && weather.data.days[dayKey] && (
                  <DayWeather
                    date={dayKey}
                    day={weather.data.days[dayKey]}
                    prefs={weather.prefs}
                    variant="header"
                    onOpen={onOpenWeather}
                  />
                )}
                {onOpenDailyNote && (hasNote || dayKey === todayKey) && (
                  <DailyNotePin
                    date={dayKey}
                    hasNote={hasNote}
                    isToday={dayKey === todayKey}
                    variant="header"
                    onOpen={onOpenDailyNote}
                  />
                )}
                <button
                  type="button"
                  aria-label="new event"
                  onClick={(ev) => {
                    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                    onCreateAt?.(day, rect);
                  }}
                  className="grid h-5 w-5 place-items-center rounded text-[var(--text-faint)] opacity-0 hover:bg-[var(--bg-hover)] hover:text-[var(--text)] group-hover:opacity-100"
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day bars */}
      <div
        className="grid border-b border-[var(--border)] bg-[var(--bg)]"
        style={{
          gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))`,
          minHeight: allDayHeight
        }}
      >
        <div className="flex items-start justify-end border-r border-[var(--border)] px-2 pt-1 text-[0.7rem] text-[var(--text-faint)]">
          all-day
        </div>
        <div ref={allDayGridRef} className="relative col-span-7" style={{ minHeight: allDayHeight }}>
          {/* Column dividers */}
          {Array.from({ length: 6 }, (_, i) => (
            <div
              key={i}
              aria-hidden
              className="pointer-events-none absolute top-0 bottom-0 border-r border-[var(--border)]"
              style={{ left: `calc(${((i + 1) / 7) * 100}% - 0.5px)` }}
            />
          ))}
          {allDayBars.map((b) => {
            const widthPct = ((b.endCol - b.startCol + 1) / 7) * 100;
            const leftPct = (b.startCol / 7) * 100;
            const bg = retintEventBackground(b.event.background_color, theme);
            const fg = retintEventForeground(b.event.background_color, theme);
            const myDrag = drag?.eventId === b.event.id && drag.kind === 'move-allday' ? drag : null;
            const movable = !!onMoveEvent && !!canMoveEvent?.(b.event);
            return (
              <button
                key={`${b.event.id}-${b.startCol}`}
                type="button"
                title={b.event.summary ?? ''}
                onMouseDown={(ev) => beginAllDayMove(b.event, ev)}
                onClick={(ev) => {
                  if (consumeDragClick()) {
                    ev.preventDefault();
                    ev.stopPropagation();
                    return;
                  }
                  const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                  onPickEvent?.(b.event.id, rect);
                }}
                onContextMenu={(ev) => {
                  if (!onEventContextMenu) return;
                  ev.preventDefault();
                  onEventContextMenu(b.event.id, ev.clientX, ev.clientY);
                }}
                className={clsx(
                  'absolute flex items-center overflow-hidden whitespace-nowrap rounded-[3px] px-[6px] text-left text-[0.72rem] font-medium leading-snug hover:brightness-95',
                  movable && 'cursor-grab',
                  myDrag && 'z-10 opacity-90 shadow-md ring-1 ring-[var(--accent)]'
                )}
                style={{
                  left: `calc(${leftPct}% + 3px)`,
                  width: `calc(${widthPct}% - 6px)`,
                  top: 4 + b.row * 22,
                  height: 20,
                  background: bg,
                  color: fg,
                  transform: myDrag ? `translateX(${myDrag.daysDelta * colWidth}px)` : undefined
                }}
              >
                {b.event.summary ?? '(no title)'}
              </button>
            );
          })}
        </div>
      </div>

      {/* Hours grid (scrollable) */}
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        <div
          ref={hoursGridRef}
          className="relative grid"
          style={{
            gridTemplateColumns: `${TIME_COL_WIDTH}px repeat(7, minmax(0, 1fr))`,
            minHeight: HOURS * hourHeight
          }}
        >
          {/* Time axis */}
          <div className="relative border-r border-[var(--border)]">
            {hourLabels.map((label, h) => (
              <div
                key={h}
                className="absolute right-2 -translate-y-1/2 text-[0.7rem] text-[var(--text-faint)]"
                style={{ top: h * hourHeight }}
              >
                {h === 0 ? '' : label}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day, dayIdx) => {
            const isToday = isSameDay(day, today);
            const key = format(day, 'yyyy-MM-dd');
            const list = (eventsByDay.get(key) ?? []).slice().sort(
              (a, b) => (a.start_time ?? '').localeCompare(b.start_time ?? '')
            );
            const laid = layoutDay(list);
            return (
              <div
                key={day.toISOString()}
                className="group relative border-r border-[var(--border)] last:border-r-0"
                onDoubleClick={(ev) => {
                  // Quick create at the clicked time slot.
                  const target = ev.currentTarget as HTMLElement;
                  const rect = target.getBoundingClientRect();
                  const relY = ev.clientY - rect.top;
                  const hour = Math.max(0, Math.min(23, Math.floor(relY / hourHeight)));
                  const slot = new Date(day);
                  slot.setHours(hour, 0, 0, 0);
                  onCreateAt?.(slot, rect);
                }}
              >
                {/* One-shot flash marking a day the user jumped to */}
                {dayPulse && isSameDay(day, dayPulse.day) && (
                  <div
                    key={`pulse-${dayPulse.key}`}
                    aria-hidden
                    className="today-pulse pointer-events-none absolute inset-0"
                    style={{ background: 'var(--today-fg)' }}
                  />
                )}
                {/* Hour gridlines — faded so column/row borders read stronger */}
                {Array.from({ length: HOURS }, (_, h) => (
                  <div
                    key={h}
                    aria-hidden
                    className="pointer-events-none absolute left-0 right-0 border-t border-[var(--border)] opacity-30"
                    style={{ top: h * hourHeight }}
                  />
                ))}
                {/* Now line */}
                {isToday && (
                  <div
                    aria-hidden
                    className="pointer-events-none absolute left-0 right-0 z-10"
                    style={{ top: (nowMin / 60) * hourHeight, height: 0 }}
                  >
                    <div className="h-px w-full bg-[var(--today-fg)]" />
                    <div
                      className="absolute -left-[3px] -top-[3px] h-[7px] w-[7px] rounded-full"
                      style={{ background: 'var(--today-fg)' }}
                    />
                  </div>
                )}
                {/* Events */}
                {laid.map((l) => {
                  const baseTop = (l.startMin / 60) * hourHeight;
                  const baseHeight = Math.max(18, ((l.endMin - l.startMin) / 60) * hourHeight - 2);
                  const widthPct = 100 / l.cols;
                  const leftPct = l.col * widthPct;
                  const bg = retintEventBackground(l.event.background_color, theme);
                  const fg = retintEventForeground(l.event.background_color, theme);
                  const myDrag = drag?.eventId === l.event.id ? drag : null;
                  const movable = !!onMoveEvent && !!canMoveEvent?.(l.event);
                  const transform =
                    myDrag?.kind === 'move-timed'
                      ? `translate(${myDrag.daysDelta * colWidth}px, ${(myDrag.deltaMin / 60) * hourHeight}px)`
                      : undefined;
                  const adjHeight =
                    myDrag?.kind === 'resize-end'
                      ? Math.max(18, baseHeight + (myDrag.deltaMin / 60) * hourHeight)
                      : baseHeight;
                  let displayStart = new Date(l.event.start_time!);
                  let displayEnd = new Date(l.event.end_time!);
                  if (myDrag?.kind === 'move-timed') {
                    displayStart = new Date(displayStart.getTime() + myDrag.deltaMin * 60_000);
                    displayEnd = new Date(displayEnd.getTime() + myDrag.deltaMin * 60_000);
                  } else if (myDrag?.kind === 'resize-end') {
                    displayEnd = new Date(displayEnd.getTime() + myDrag.deltaMin * 60_000);
                    const minEnd = new Date(displayStart.getTime() + 15 * 60_000);
                    if (displayEnd < minEnd) displayEnd = minEnd;
                  }
                  return (
                    <button
                      key={l.event.id}
                      type="button"
                      title={l.event.summary ?? ''}
                      onMouseDown={(ev) => {
                        if (!movable) return;
                        const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                        const fromBottom = r.bottom - ev.clientY;
                        if (fromBottom <= RESIZE_HANDLE_PX) {
                          ev.preventDefault();
                          beginTimedResize(l.event, ev);
                        } else {
                          beginTimedMove(l.event, dayIdx, ev);
                        }
                      }}
                      onClick={(ev) => {
                        if (consumeDragClick()) {
                          ev.preventDefault();
                          ev.stopPropagation();
                          return;
                        }
                        ev.stopPropagation();
                        const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
                        onPickEvent?.(l.event.id, rect);
                      }}
                      onContextMenu={(ev) => {
                        if (!onEventContextMenu) return;
                        ev.preventDefault();
                        ev.stopPropagation();
                        onEventContextMenu(l.event.id, ev.clientX, ev.clientY);
                      }}
                      className={clsx(
                        'absolute flex flex-col items-start overflow-hidden whitespace-nowrap rounded-[4px] p-[5px] text-left text-[0.72rem] leading-tight hover:brightness-95',
                        movable && 'cursor-grab',
                        myDrag && 'z-10 shadow-md ring-1 ring-[var(--accent)]'
                      )}
                      style={{
                        top: baseTop,
                        height: adjHeight,
                        left: `calc(${leftPct}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        background: bg,
                        color: fg,
                        transform
                      }}
                    >
                      <div className="text-[0.65rem] opacity-85">
                        {formatTimeRange(displayStart, displayEnd)}
                      </div>
                      <div className="font-medium overflow-hidden text-ellipsis">
                        {l.event.summary ?? '(no title)'}
                      </div>
                      {movable && (
                        <div
                          aria-hidden
                          className="absolute bottom-0 left-0 right-0 cursor-ns-resize"
                          style={{ height: RESIZE_HANDLE_PX }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {weather?.prefs.overlays.includes('week_graph') && (
        <WeekWeatherRow days={days} weather={weather} gutter={TIME_COL_WIDTH} onOpen={onOpenWeather} />
      )}
    </div>
  );
}

